import React from "react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@coval/ui"

import type { MetricRecordData } from "@/types/metrics/contracts"

import {
  FIELD_CONTAINER_STYLES,
  LABEL_ROW_STYLES,
  LABEL_STYLES,
  TEXT_INPUT_STYLES,
} from "../styles"
import { RequiredMarker } from "./RequiredMarker"

interface RegexConfigProps {
  metricRecordData: MetricRecordData
  onChange: (metric: MetricRecordData) => void
}

const roles = [
  { key: "agent", label: "AGENT" },
  { key: "persona", label: "PERSONA" },
  { key: "tool", label: "TOOL" },
  { key: "system", label: "SYSTEM" },
  { key: "music", label: "MUSIC" },
]

const matchModes = [
  { key: "presence", label: "Presence (match found = pass)" },
  { key: "absence", label: "Absence (match NOT found = pass)" },
]

const positions = [
  { key: "any", label: "Any message" },
  { key: "first", label: "First message only" },
  { key: "last", label: "Last message only" },
]

interface RoleSelectorProps {
  value?: string
  onChange: (role: string) => void
}

function RoleSelector({ value, onChange }: RoleSelectorProps) {
  return (
    <Select value={value || ""} onValueChange={onChange}>
      <SelectTrigger aria-label="Select a role">
        <SelectValue placeholder="Select a role" />
      </SelectTrigger>
      <SelectContent>
        {roles.map((role) => (
          <SelectItem key={role.key} value={role.key}>
            {role.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export const RegexConfig: React.FC<RegexConfigProps> = ({
  metricRecordData,
  onChange,
}) => {
  const handleRegexPatternChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        regex_pattern: e.target.value,
      },
    })
  }

  const handleRoleChange = (role: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        role: role as "agent" | "persona",
      },
    })
  }

  const handleMatchModeChange = (matchMode: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        match_mode: matchMode as "presence" | "absence",
      },
    })
  }

  const handlePositionChange = (position: string) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        position: position as "any" | "first" | "last",
      },
    })
  }

  const handleCaseInsensitiveToggle = (enabled: boolean) => {
    onChange({
      ...metricRecordData,
      metric_metadata: {
        ...metricRecordData.metric_metadata,
        case_insensitive: enabled,
      },
    })
  }

  const caseInsensitive =
    metricRecordData.metric_metadata?.case_insensitive || false

  return (
    <div className="space-y-4">
      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>
            Regex Pattern
            <RequiredMarker />
          </label>
        </div>
        <input
          type="text"
          className={TEXT_INPUT_STYLES}
          value={metricRecordData.metric_metadata?.regex_pattern || ""}
          onChange={handleRegexPatternChange}
          placeholder="The regex pattern to match against transcript messages"
        />
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>
            Role
            <RequiredMarker />
          </label>
        </div>
        <RoleSelector
          value={metricRecordData.metric_metadata?.role}
          onChange={handleRoleChange}
        />
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Match Mode</label>
        </div>
        <Select
          value={metricRecordData.metric_metadata?.match_mode || "presence"}
          onValueChange={handleMatchModeChange}
        >
          <SelectTrigger aria-label="Select match mode">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {matchModes.map((mode) => (
              <SelectItem key={mode.key} value={mode.key}>
                {mode.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Position</label>
        </div>
        <Select
          value={metricRecordData.metric_metadata?.position || "any"}
          onValueChange={handlePositionChange}
        >
          <SelectTrigger aria-label="Select position">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {positions.map((pos) => (
              <SelectItem key={pos.key} value={pos.key}>
                {pos.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className={FIELD_CONTAINER_STYLES}>
        <div className={LABEL_ROW_STYLES}>
          <label className={LABEL_STYLES}>Case Sensitivity</label>
        </div>
        <div className="flex h-control rounded-md bg-surface-1 p-1">
          <button
            type="button"
            onClick={() => handleCaseInsensitiveToggle(false)}
            className={`flex-1 px-3 text-sm font-medium rounded-sm transition-all ${
              !caseInsensitive
                ? "bg-surface-default text-coval-text shadow-sm"
                : "text-coval-text-muted hover:text-coval-text"
            }`}
          >
            Case Sensitive
          </button>
          <button
            type="button"
            onClick={() => handleCaseInsensitiveToggle(true)}
            className={`flex-1 px-3 text-sm font-medium rounded-sm transition-all ${
              caseInsensitive
                ? "bg-surface-default text-coval-text shadow-sm"
                : "text-coval-text-muted hover:text-coval-text"
            }`}
          >
            Case Insensitive
          </button>
        </div>
      </div>
    </div>
  )
}
